// =============================================================================
// bookingTokens.ts — Bookings V2 design tokens (single source of truth)
//
// FORT-178 SEAM
// -------------
// Every colour / radius literal that Bookings V2 renders lives here, in ONE
// place. Components (the MUI grid cell, the grid chrome) import from this module
// instead of inlining hex. When FORT-178 introduces the global Fortius token
// system, this is the only file that changes.
//
// These values are the **Member Profile V2 ("Fortius") language** — the same
// tokens as `components/membership/profile/v2/fortius-profile.css` and
// `v2/fortiusTheme.ts`: teal accent, ink/muted/line neutrals, the
// success/warning/error status trio, radius-12 surfaces, and NO custom font
// (the admin app's font stays; only size/weight vary). The teal is imported from
// the profile-V2 theme so the two screens literally share one accent source.
//
// The Ant Design modal/chrome side reads the same values via CSS custom
// properties scoped under `.ft-bookings-v2` (fortius-bookings.css) and reuses the
// exported `fortiusProfileTheme` (bookingsV2Theme.ts). Keep the three in sync.
// =============================================================================

import { FORTIUS_TEAL } from '../../membership/profile/v2/fortiusTheme';

/** Core Fortius palette — teal accent, ink/muted/line neutrals, status trio. */
export const bookingColors = {
  ink: '#1f2a2e',
  inkMuted: '#5b6b70',
  inkFaint: '#7d9890',

  teal: FORTIUS_TEAL, // #0d7c73
  tealDark: '#0a5c55',
  tealTint: '#e6f4f2',

  success: '#1f8a5b',
  warning: '#b7791f',
  error: '#c03a2b',

  line: '#e6eaec',
  appBg: '#eef6f3', // V2 screen canvas
  surfaceTint: '#f6f9f9', // table header + meta strip
  bgCard: '#ffffff',

  owed: '#c03a2b',
} as const;

/**
 * Slot status fills. Soft tint + a 4px left stripe in the V2 status colour
 * (mirrors the `.ft-stripe` device on the profile cards).
 *   base   = resting fill
 *   border = 1px edge
 *   hover  = darker fill on hover (disabled cells don't hover)
 *   stripe = 4px left bar colour (booking cards only)
 *   ink    = label colour for the stateless (available / past / blocked) variants
 */
export const slotPalette = {
  booked: { base: '#EAF5EF', border: '#CFE6DA', hover: '#DFEFE6', stripe: '#1f8a5b', ink: '#5b6b70' },
  pending: { base: '#FBF2E0', border: '#EDD9B0', hover: '#F6E9CC', stripe: '#b7791f', ink: '#5b6b70' },
  overdue: { base: '#FBE9E5', border: '#F0CBC3', hover: '#F6DAD3', stripe: '#c03a2b', ink: '#5b6b70' },
  available: { base: '#FFFFFF', border: '#CFE0DB', hover: '#E6F4F2', stripe: 'transparent', ink: '#7d9890' },
  past: { base: '#F3F6F6', border: '#E6EAEC', hover: '#F3F6F6', stripe: 'transparent', ink: '#9aa9ab' },
  blocked: { base: '#EEF1F1', border: '#E6EAEC', hover: '#E7ECEC', stripe: '#b9c4c4', ink: '#5b6b70' },
} as const;

/** Past-slot diagonal stripe (rendered as a repeating-linear-gradient). */
export const pastStripe =
  'repeating-linear-gradient(135deg, #F3F6F6 0 6px, #EAEFEF 6px 12px)';

/** Payment status tag pills (bottom-right of a booking card / modal header). */
export const paymentTagPalette = {
  paid: { bg: '#E3F2EA', fg: '#1F8A5B', label: 'Paid' },
  pending: { bg: '#F7EAD0', fg: '#9A6712', label: 'Pending' },
  unpaid: { bg: '#F7DDD6', fg: '#B8301F', label: 'Unpaid' },
} as const;

/** Radii — V2 metrics: card 8, container/surface 12, tag 6. */
export const bookingRadii = {
  cell: '8px',
  container: '12px',
  tag: '6px',
} as const;

export type SlotKind = keyof typeof slotPalette;
export type PaymentTag = keyof typeof paymentTagPalette;
